<template>
  <!-- display: contents lets the sticky nav stick against the whole page
       rather than this wrapper. -->
  <div class="app-header">
    <div class="announcement-bar" aria-live="polite">
      <transition name="fade" mode="out-in">
        <p :key="announcementIndex">{{ announcements[announcementIndex] }}</p>
      </transition>
    </div>

    <header class="site-nav">
      <div class="site-nav__inner">
        <button
          class="site-nav__icon-btn site-nav__burger"
          :aria-expanded="mobileOpen ? 'true' : 'false'"
          aria-controls="mobile-menu"
          aria-label="Open menu"
          @click="mobileOpen = true"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true">
            <path d="M3 7h18M3 12h18M3 17h18"/>
          </svg>
        </button>

        <router-link to="/" class="site-nav__logo" aria-label="Walls & Faces — Home">
          Walls <span>&amp;</span> Faces
        </router-link>

        <nav class="site-nav__menu" aria-label="Main">
          <div
            v-for="(menu, i) in menus"
            :key="menu.label"
            class="site-nav__item"
            :class="{ 'is-open': openMenu === i }"
            @mouseenter="openMenu = i"
            @mouseleave="openMenu = null"
            @focusout="onMenuFocusOut($event, i)"
          >
            <button
              class="site-nav__link"
              :aria-expanded="openMenu === i ? 'true' : 'false'"
              @click="openMenu = openMenu === i ? null : i"
            >
              {{ menu.label }}
              <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden="true">
                <path d="M1 3l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.2"/>
              </svg>
            </button>
            <ul class="site-nav__dropdown">
              <li v-for="link in menu.links" :key="link.label">
                <router-link v-if="link.to.startsWith('/')" :to="link.to" @click="openMenu = null">{{ link.label }}</router-link>
                <a v-else :href="link.to">{{ link.label }}</a>
              </li>
            </ul>
          </div>
        </nav>

        <div class="site-nav__actions">
          <a :href="accountUrl" class="site-nav__text-link site-nav__account">Account</a>

          <button class="site-nav__text-link site-nav__search-btn" :aria-expanded="searchOpen ? 'true' : 'false'" @click="toggleSearch">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/>
            </svg>
            <span class="site-nav__search-label">Search</span>
          </button>

          <button class="site-nav__text-link site-nav__cart" aria-label="View cart" @click="openDrawer">
            <span>Cart</span>
            <span v-if="cartTotal" class="site-nav__cart-total">{{ cartTotal }}</span>
            <span class="site-nav__cart-count">({{ cart.totalQuantity }})</span>
          </button>
        </div>
      </div>

      <form v-if="searchOpen" class="site-search" role="search" @submit.prevent="submitSearch">
        <label class="sr-only" for="site-search-input">Search the shop</label>
        <input
          id="site-search-input"
          ref="searchInput"
          v-model="searchTerm"
          type="search"
          placeholder="Search paintings, ceramics, rugs…"
          @keydown.esc="searchOpen = false"
        />
        <button type="submit" class="btn btn--dark">Search</button>
      </form>

      <HarlequinBorder />
    </header>

    <!-- Mobile menu -->
    <transition name="slide">
      <div v-if="mobileOpen" id="mobile-menu" class="mobile-menu" role="dialog" aria-modal="true" aria-label="Menu">
        <div class="mobile-menu__head">
          <span class="site-nav__logo">Walls <span>&amp;</span> Faces</span>
          <button class="site-nav__icon-btn" aria-label="Close menu" @click="mobileOpen = false">✕</button>
        </div>

        <div v-for="(menu, i) in menus" :key="menu.label" class="mobile-menu__group">
          <button
            class="mobile-menu__toggle"
            :aria-expanded="mobileSection === i ? 'true' : 'false'"
            @click="mobileSection = mobileSection === i ? null : i"
          >
            {{ menu.label }}
            <span aria-hidden="true">{{ mobileSection === i ? '−' : '+' }}</span>
          </button>
          <ul v-show="mobileSection === i" class="mobile-menu__links">
            <li v-for="link in menu.links" :key="link.label">
              <router-link v-if="link.to.startsWith('/')" :to="link.to" @click="mobileOpen = false">{{ link.label }}</router-link>
              <a v-else :href="link.to">{{ link.label }}</a>
            </li>
          </ul>
        </div>

        <a :href="accountUrl" class="mobile-menu__toggle">Account</a>
      </div>
    </transition>
    <div v-if="mobileOpen" class="mobile-menu__backdrop" @click="mobileOpen = false"></div>

    <CartDrawer :open="cart.drawerOpen" @close="closeDrawer" />
  </div>
</template>

<script>
import { useCart } from '../shopify/cart'
import { formatMoney } from '../shopify/money'
import { ANNOUNCEMENTS, MAIN_MENU, ACCOUNT_URL } from '../navigation'
import CartDrawer from './CartDrawer.vue'
import HarlequinBorder from './HarlequinBorder.vue'

export default {
  name: 'AppHeader',

  components: { CartDrawer, HarlequinBorder },

  setup() {
    const { state: cart, loadCart, openDrawer, closeDrawer } = useCart()
    return { cart, loadCart, openDrawer, closeDrawer }
  },

  data() {
    return {
      announcements: ANNOUNCEMENTS,
      announcementIndex: 0,
      menus: MAIN_MENU,
      accountUrl: ACCOUNT_URL,
      openMenu: null,
      mobileOpen: false,
      mobileSection: 0,
      searchOpen: false,
      searchTerm: '',
    }
  },

  computed: {
    cartTotal() {
      const total = this.cart.cost?.totalAmount
      if (!total || !this.cart.totalQuantity) return ''
      return formatMoney(total.amount, total.currencyCode)
    },
  },

  watch: {
    $route() {
      this.openMenu = null
      this.mobileOpen = false
      this.searchOpen = false
    },
  },

  mounted() {
    this.loadCart()
    this.announcementTimer = setInterval(() => {
      this.announcementIndex = (this.announcementIndex + 1) % this.announcements.length
    }, 5000)
  },

  unmounted() {
    clearInterval(this.announcementTimer)
  },

  methods: {
    // Close a dropdown once keyboard focus leaves it entirely.
    onMenuFocusOut(event, i) {
      if (!event.currentTarget.contains(event.relatedTarget) && this.openMenu === i) {
        this.openMenu = null
      }
    },

    toggleSearch() {
      this.searchOpen = !this.searchOpen
      if (this.searchOpen) this.$nextTick(() => this.$refs.searchInput?.focus())
    },

    submitSearch() {
      const q = this.searchTerm.trim()
      this.$router.push({ path: '/shop', query: q ? { q } : {} })
    },
  },
}
</script>

<style scoped>
.app-header { display: contents; }

/* ── Announcement bar ──────────────────────────────────── */
.announcement-bar {
  background: var(--amber-deep);
  color: var(--canvas);
  text-align: center;
  padding: 0.9rem 2rem;
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  min-height: 3.6rem;
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.4s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* ── Main nav ──────────────────────────────────────────── */
.site-nav {
  position: sticky;
  top: 0;
  z-index: 250;
  background: var(--canvas);
}

.site-nav__inner {
  display: flex;
  align-items: center;
  gap: 4.8rem;
  max-width: var(--max-w);
  margin: 0 auto;
  padding: 0 4rem;
  height: 8rem;
}

.site-nav__logo {
  font-family: var(--font-display);
  font-size: 2.8rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: var(--ink);
  white-space: nowrap;
}

.site-nav__logo span {
  font-style: italic;
  color: var(--amber);
}

.site-nav__menu {
  display: flex;
  align-items: stretch;
  height: 100%;
  gap: 3.2rem;
}

.site-nav__item {
  position: relative;
  display: flex;
  align-items: center;
}

.site-nav__link,
.site-nav__text-link {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  background: none;
  border: none;
  padding: 0;
  font-family: var(--font);
  font-size: 1.2rem;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--ink);
  cursor: pointer;
}

.site-nav__link:hover,
.site-nav__text-link:hover,
.site-nav__item.is-open .site-nav__link { color: var(--amber); }

.site-nav__link svg { transition: transform 0.2s ease; }
.site-nav__item.is-open .site-nav__link svg { transform: rotate(180deg); }

.site-nav__dropdown {
  position: absolute;
  top: 100%;
  left: -2.4rem;
  min-width: 22rem;
  list-style: none;
  padding: 1.6rem 2.4rem 2rem;
  background: var(--canvas);
  border: 1px solid var(--linen);
  border-top: none;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-4px);
  transition: opacity 0.18s ease, transform 0.18s ease, visibility 0.18s;
}

.site-nav__item.is-open .site-nav__dropdown {
  opacity: 1;
  visibility: visible;
  transform: none;
}

.site-nav__dropdown a {
  display: block;
  padding: 0.7rem 0;
  font-family: var(--font-display);
  font-size: 1.9rem;
  color: var(--ink);
  transition: color 0.25s ease, transform 0.25s ease;
}
.site-nav__dropdown a:hover { color: var(--amber); transform: translateX(0.4rem); }

.site-nav__actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 2.8rem;
}

.site-nav__cart { gap: 0.4rem; }

.site-nav__icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  background: none;
  border: none;
  font-size: 1.8rem;
  color: var(--ink);
  cursor: pointer;
}

.site-nav__burger { display: none; }

/* ── Search ────────────────────────────────────────────── */
.site-search {
  display: flex;
  gap: 1rem;
  max-width: var(--max-w);
  margin: 0 auto;
  padding: 0 4rem 2rem;
}

.site-search input {
  flex: 1;
  padding: 1.2rem 0;
  border: none;
  border-bottom: 1px solid var(--ink);
  background: transparent;
  font-family: var(--font-display);
  font-size: 2.2rem;
  color: var(--ink);
  outline: none;
}
.site-search input::placeholder { color: var(--warm-grey); font-style: italic; }

/* ── Mobile menu ───────────────────────────────────────── */
.mobile-menu {
  position: fixed;
  inset: 0 auto 0 0;
  width: min(36rem, 88vw);
  z-index: 400;
  background: var(--canvas);
  padding: 1.6rem 2.4rem 4rem;
  overflow-y: auto;
}

.mobile-menu__backdrop {
  position: fixed;
  inset: 0;
  z-index: 390;
  background: rgba(43, 36, 32, 0.4);
}

.mobile-menu__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2.4rem;
}

.mobile-menu__toggle {
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 1.6rem 0;
  background: none;
  border: none;
  border-bottom: 1px solid var(--linen);
  font-family: var(--font);
  font-size: 1.3rem;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--ink);
  cursor: pointer;
}

.mobile-menu__links {
  list-style: none;
  padding: 0.8rem 0 1.6rem;
}

.mobile-menu__links a {
  display: block;
  padding: 0.8rem 0;
  font-family: var(--font-display);
  font-size: 2.2rem;
  color: var(--ink);
}

.slide-enter-active, .slide-leave-active { transition: transform 0.3s ease; }
.slide-enter-from, .slide-leave-to { transform: translateX(-100%); }

@media (prefers-reduced-motion: reduce) {
  .fade-enter-active, .fade-leave-active,
  .slide-enter-active, .slide-leave-active,
  .site-nav__dropdown { transition: none; }
}

@media (max-width: 960px) {
  .site-nav__inner {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 1rem;
    height: 6.4rem;
    padding: 0 1.6rem;
  }

  .site-nav__burger { display: flex; margin-left: -1rem; }
  .site-nav__menu,
  .site-nav__account,
  .site-nav__search-label,
  .site-nav__cart-total { display: none; }

  .site-nav__logo { font-size: 2.3rem; justify-self: center; }
  .site-nav__actions { justify-self: end; gap: 1.6rem; }

  .site-search { padding: 0 1.6rem 1.6rem; }
  .site-search input { font-size: 1.8rem; }
}

@media (max-width: 48rem) {
  .announcement-bar { letter-spacing: 0.12em; font-size: 1rem; }
}
</style>
